import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, PlusCircle, TrendingUp, CreditCard } from "lucide-react";

interface StripePostConnectionForkProps {
  onSelectOption: (option: 'migrate' | 'create' | 'skip') => void;
}

const StripePostConnectionFork: React.FC<StripePostConnectionForkProps> = ({ onSelectOption }) => {

  return (
    <div className="flex flex-col items-center justify-center py-4 space-y-4">
      <div className="h-12 w-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-7 w-7"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-1 text-gray-900">Stripe Connected!</h2>
        <p className="text-gray-600 text-sm mb-4">
          Your Stripe account has been connected successfully. You can now start recovering fees.
        </p>
      </div>

      {/* Fork Options */}
      <div className="w-full max-w-2xl space-y-3">
        <h3 className="text-base font-medium text-center text-gray-800 mb-3">
          What would you like to do now?
        </h3>

        <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-keepmore-purple/20" 
              onClick={() => onSelectOption('migrate')}>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-base font-semibold text-gray-900 mb-1">
                Migrate existing subscriptions
              </h4>
              <p className="text-gray-600 text-sm mb-2">
                Stop losing fees on payments you're already collecting. 
                Migrate your existing subscriptions to start recovering fees immediately.
              </p>
              <div className="flex items-center text-xs text-blue-600 font-medium">
                <TrendingUp className="h-3 w-3 mr-1" />
                Recover fees from existing payments
                <ArrowRight className="h-3 w-3 ml-1" />
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-keepmore-purple/20" 
              onClick={() => onSelectOption('create')}>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 bg-green-100 text-green-600 rounded-lg flex items-center justify-center">
                <PlusCircle className="h-5 w-5" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-base font-semibold text-gray-900 mb-1">
                Create new products
              </h4>
              <p className="text-gray-600 text-sm mb-2">
                Secure new sales with automatic fee recovery. 
                Create products optimized to maximize your income.
              </p>
              <div className="flex items-center text-xs text-green-600 font-medium">
                <CreditCard className="h-3 w-3 mr-1" />
                Optimize future sales
                <ArrowRight className="h-3 w-3 ml-1" />
              </div>
            </div>
          </div>
        </Card>
      </div>
      <div className="text-center pt-2">
        <button
          onClick={() => onSelectOption('skip')}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Continue later
        </button>
      </div>
    </div>
  );
};

export default StripePostConnectionFork;
